import React, { useState } from 'react'
import {
  AppBar, Toolbar, Typography, Container, Box, Tab, Tabs,
  Card, CardContent, Grid, Chip, Button, CircularProgress, Alert,
  TextField, InputAdornment, Pagination, FormControl, InputLabel, 
  Select, MenuItem, Stack, LinearProgress, Divider
} from '@mui/material'
import { Search as SearchIcon, TrendingUp, Assessment, Timeline } from '@mui/icons-material'
import { useQuery } from 'react-query'
import toast from 'react-hot-toast'

const API_BASE_URL = 'https://getmyhive-news-intelligence-production.up.railway.app'

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

function App() {
  const [activeTab, setActiveTab] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(20)

  const { data: apiStatus, isLoading } = useQuery(
    'apiStatus',
    async () => {
      const response = await fetch(`${API_BASE_URL}/`)
      return response.json()
    },
    { refetchInterval: 30000 }
  )

  const { data: sourcesData } = useQuery(
    'sources',
    async () => {
      const response = await fetch(`${API_BASE_URL}/api/news/sources`)
      return response.json()
    }
  )

  const { data: articlesData, isLoading: articlesLoading } = useQuery(
    ['articles', searchQuery, currentPage, itemsPerPage],
    async () => {
      const offset = (currentPage - 1) * itemsPerPage
      let url = `${API_BASE_URL}/api/news/articles?limit=${itemsPerPage}&offset=${offset}`
      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery)}`
      }
      const response = await fetch(url)
      return response.json()
    },
    { refetchInterval: 30000 }
  )

  const { data: analyticsData, isLoading: analyticsLoading } = useQuery(
    'analytics',
    async () => {
      const response = await fetch(`${API_BASE_URL}/api/analytics/sentiment-overview`)
      return response.json()
    },
    { refetchInterval: 60000 }
  )

  // Gelişmiş şiddet seviyesi analizi (0-100 skala)
  const { data: severityData, isLoading: severityLoading } = useQuery(
    'severityDistribution',
    async () => {
      const response = await fetch(`${API_BASE_URL}/api/analytics/severity-distribution`)
      return response.json()
    },
    { refetchInterval: 120000 }
  )

  // En çok geçen kelimeler
  const { data: keywordData, isLoading: keywordLoading } = useQuery(
    'keywordFrequency',
    async () => {
      const response = await fetch(`${API_BASE_URL}/api/analytics/keyword-frequency?limit=15`)
      return response.json()
    },
    { refetchInterval: 180000 }
  )

  // Yıllık timeline verileri - Mevcut verilerden oluştur
  const { data: yearlyTimelineData, isLoading: yearlyTimelineLoading } = useQuery(
    'yearlyTimeline',
    async () => {
      try {
        // Önce timeline endpoint'ini dene
        const timelineResponse = await fetch(`${API_BASE_URL}/api/analytics/timeline-yearly`)
        if (timelineResponse.ok) {
          return await timelineResponse.json()
        }
        
        // Eğer çalışmıyorsa, mevcut verilerden timeline oluştur
        const articlesResponse = await fetch(`${API_BASE_URL}/api/news/articles?limit=1000`)
        const articlesData = await articlesResponse.json()
        
        if (articlesData.articles) {
          // Verilerden yıllık analiz yap
          const yearlyData = {}
          let totalDeaths = 0
          let totalInjuries = 0
          let totalViolence = 0
          
          articlesData.articles.forEach(article => {
            const year = new Date(article.created_at).getFullYear()
            if (!yearlyData[year]) {
              yearlyData[year] = {
                year: year,
                total_articles: 0,
                violence_articles: 0,
                death_articles: 0,
                injury_articles: 0
              }
            }
            
            yearlyData[year].total_articles++
            
            if (article.category === 'şiddet') {
              yearlyData[year].violence_articles++
              totalViolence++
            }
            
            // Ölüm haberleri
            if (article.title && (
              article.title.toLowerCase().includes('ölüm') ||
              article.title.toLowerCase().includes('cinayet') ||
              article.title.toLowerCase().includes('öldürme')
            )) {
              yearlyData[year].death_articles++
              totalDeaths++
            }
            
            // Yaralama haberleri
            if (article.title && (
              article.title.toLowerCase().includes('yaralama') ||
              article.title.toLowerCase().includes('darp') ||
              article.title.toLowerCase().includes('saldırı')
            )) {
              yearlyData[year].injury_articles++
              totalInjuries++
            }
          })
          
          const timeline = Object.values(yearlyData).sort((a, b) => b.year - a.year)
          
          return {
            timeline: timeline,
            summary: {
              total_articles: articlesData.total || articlesData.articles.length,
              total_violence: totalViolence,
              total_deaths: totalDeaths,
              total_injuries: totalInjuries,
              years_covered: timeline.length
            },
            message: "Mevcut verilerden timeline oluşturuldu",
            fallback: true
          }
        }
        
        return { error: "Veri bulunamadı" }
      } catch (error) {
        console.error('Timeline error:', error)
        return { error: "Timeline verisi alınamadı" }
      }
    },
    { refetchInterval: 300000 }
  )

  // Gelişmiş sentiment analizi çalıştırma
  const runAdvancedAnalysis = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/advanced-sentiment`, {
        method: 'POST'
      })
      const result = await response.json()
      console.log('Advanced analysis completed:', result)
      // Verileri yenile
      window.location.reload()
    } catch (error) {
      console.error('Advanced analysis error:', error)
    }
  }

  // 10 yıllık geçmiş veri toplama
  const runHistoricalScraping = async () => {
    try {
      toast.loading('Geçmiş veri toplama başlatılıyor...', { duration: 2000 })
      
      const response = await fetch(`${API_BASE_URL}/api/data/historical-scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ years_back: 10 })
      })
      const result = await response.json()
      
      if (result.status === 'completed') {
        toast.success(`Geçmiş veri toplama tamamlandı! ${result.results?.total_articles || 0} haber toplandı.`)
      } else {
        toast.error('Geçmiş veri toplama sırasında hata oluştu.')
      }
      
      console.log('Historical scraping completed:', result)
      // Verileri yenile
      setTimeout(() => window.location.reload(), 2000)
    } catch (error) {
      console.error('Historical scraping error:', error)
      toast.error('Geçmiş veri toplama hatası: ' + error.message)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <AppBar position="static" sx={{ background: 'rgba(255,255,255,0.95)', color: '#333' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            📰 GetMyHive News Intelligence v1.0.1
          </Typography>
          {apiStatus && (
            <Chip label={`API: ${apiStatus.status}`} color="success" size="small" />
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Card sx={{ mb: 3, background: 'rgba(255,255,255,0.95)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>🚀 Sistem Durumu</Typography>
            {isLoading ? (
              <CircularProgress size={20} />
            ) : apiStatus ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip label={`Status: ${apiStatus.status}`} color="success" />
                <Chip label={`Database: ${apiStatus.database}`} color="info" />
                <Chip label={`Version: ${apiStatus.version}`} variant="outlined" />
              </Box>
            ) : (
              <Alert severity="error">API bağlantısı kurulamadı</Alert>
            )}
          </CardContent>
        </Card>

        <Card sx={{ background: 'rgba(255,255,255,0.95)' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="Dashboard" />
            <Tab label="Haberler" />
            <Tab label="Timeline" />
            <Tab label="Haber Kaynakları" />
            <Tab label="Analitik" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Typography variant="h6" gutterBottom>📊 Dashboard</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Toplam Haberler</Typography>
                    <Typography variant="h4" color="success.main">
                      {articlesData?.total || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Sistemde toplanan tüm haberler
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Aktif Kaynaklar</Typography>
                    <Typography variant="h4" color="primary">
                      {sourcesData?.sources?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Haber toplanan kaynak sayısı
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Şiddet Haberleri</Typography>
                    <Typography variant="h4" color="error.main">
                      {articlesData?.articles ? 
                        articlesData.articles.filter(a => a.category === 'şiddet').length : 
                        '~589'
                      }
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Kadına şiddet ile ilgili
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Son Güncelleme</Typography>
                    <Typography variant="h6" color="info.main">
                      Bugün
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Sistem durumu: Aktif
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>🚀 Sistem İşlemleri</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={runAdvancedAnalysis}
                  startIcon={<Assessment />}
                  sx={{ minWidth: 200 }}
                >
                  Gelişmiş Analiz Çalıştır
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={runHistoricalScraping}
                  startIcon={<Timeline />}
                  sx={{ minWidth: 200 }}
                >
                  10 Yıllık Veri Topla
                </Button>
              </Stack>
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Typography variant="h6" gutterBottom>📰 Haberler ({articlesData?.total || articlesData?.articles?.length || 0} adet)</Typography>
            
            {/* Search ve Pagination Kontrolleri */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                placeholder="Haberlerde ara..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1) // Reset to first page on search
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Sayfa Başına</InputLabel>
                <Select
                  value={itemsPerPage}
                  label="Sayfa Başına"
                  onChange={(e) => {
                    setItemsPerPage(e.target.value)
                    setCurrentPage(1) // Reset to first page
                  }}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={60}>60</MenuItem>
                  <MenuItem value={80}>80</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {articlesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : articlesData?.articles ? (
              <Grid container spacing={2}>
                {articlesData.articles.map((article) => (
                  <Grid item xs={12} md={6} key={article.id}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
                          {article.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          {article.content?.substring(0, 150)}...
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Chip 
                              label={article.category || 'Genel'} 
                              size="small" 
                              color={article.category === 'şiddet' ? 'error' : 'primary'} 
                            />
                            <Chip 
                              label={`Kaynak: ${article.source_id}`} 
                              size="small" 
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                          <Button 
                            size="small" 
                            href={article.url} 
                            target="_blank"
                            variant="outlined"
                          >
                            Oku
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">Haberler yükleniyor...</Alert>
            )}
            
            {/* Pagination */}
            {articlesData?.articles && articlesData.articles.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={Math.ceil((articlesData?.total || articlesData.articles.length) / itemsPerPage)}
                  page={currentPage}
                  onChange={(e, page) => setCurrentPage(page)}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Typography variant="h6" gutterBottom>📅 Yıllara Göre Kadın Ölümleri & Yaralanmaları Timeline</Typography>
            
            {yearlyTimelineLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : yearlyTimelineData && !yearlyTimelineData.error ? (
              <Grid container spacing={3}>
                {/* Özet İstatistikler */}
                <Grid item xs={12}>
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        📊 10 Yıllık Özet İstatistikler ({yearlyTimelineData.period})
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={6} md={2}>
                          <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="primary">
                              {yearlyTimelineData.summary?.total_articles || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Toplam Haber
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="error.main">
                              {yearlyTimelineData.summary?.total_deaths || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Ölüm Haberleri
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="warning.main">
                              {yearlyTimelineData.summary?.total_injuries || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Yaralama Haberleri
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="success.main">
                              {yearlyTimelineData.summary?.total_violence || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Şiddet Haberleri
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="info.main">
                              {yearlyTimelineData.summary?.total_assaults || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Saldırı Haberleri
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="text.secondary">
                              {yearlyTimelineData.summary?.years_covered || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Yıl Kapsamı
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Yıllık Timeline */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        📈 Yıllara Göre Dağılım
                      </Typography>
                      <Grid container spacing={2}>
                        {yearlyTimelineData.timeline?.map((yearData) => (
                          <Grid item xs={12} md={6} lg={4} key={yearData.year}>
                            <Card variant="outlined" sx={{ height: '100%' }}>
                              <CardContent>
                                <Typography variant="h6" gutterBottom color="primary">
                                  {yearData.year}
                                </Typography>
                                
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Toplam Haber: {yearData.total_articles}
                                  </Typography>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={Math.min((yearData.total_articles / 100) * 100, 100)} 
                                    sx={{ height: 6, borderRadius: 3, mb: 1 }}
                                  />
                                </Box>

                                <Grid container spacing={1}>
                                  <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: 1 }}>
                                      <Typography variant="h6" color="error.main">
                                        {yearData.death_articles}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        Ölüm
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 1 }}>
                                      <Typography variant="h6" color="warning.main">
                                        {yearData.injury_articles}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        Yaralama
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>

                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="body2" color="textSecondary">
                                    Şiddet: {yearData.violence_articles} | 
                                    Saldırı: {yearData.assault_articles}
                                  </Typography>
                                </Box>

                                {/* Aylık detayları (collapsible) */}
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" color="textSecondary">
                                    Aylık dağılım: {yearData.months?.length || 0} ay verisi
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Trend Analizi */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        📊 Trend Analizi
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            En Yüksek Ölüm Sayısı:
                          </Typography>
                          {yearlyTimelineData.timeline && yearlyTimelineData.timeline.length > 0 ? (
                            (() => {
                              const maxDeaths = Math.max(...yearlyTimelineData.timeline.map(y => y.death_articles));
                              const yearWithMaxDeaths = yearlyTimelineData.timeline.find(y => y.death_articles === maxDeaths);
                              return (
                                <Box sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: 2 }}>
                                  <Typography variant="h5" color="error.main">
                                    {yearWithMaxDeaths.year}: {maxDeaths} ölüm
                                  </Typography>
                                </Box>
                              );
                            })()
                          ) : null}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            En Yüksek Yaralama Sayısı:
                          </Typography>
                          {yearlyTimelineData.timeline && yearlyTimelineData.timeline.length > 0 ? (
                            (() => {
                              const maxInjuries = Math.max(...yearlyTimelineData.timeline.map(y => y.injury_articles));
                              const yearWithMaxInjuries = yearlyTimelineData.timeline.find(y => y.injury_articles === maxInjuries);
                              return (
                                <Box sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
                                  <Typography variant="h5" color="warning.main">
                                    {yearWithMaxInjuries.year}: {maxInjuries} yaralama
                                  </Typography>
                                </Box>
                              );
                            })()
                          ) : null}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Alert severity="warning">
                Timeline verileri yüklenemedi. Lütfen daha sonra tekrar deneyin.
              </Alert>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Typography variant="h6" gutterBottom>📡 Haber Kaynakları</Typography>
            {sourcesData?.sources ? (
              <Grid container spacing={2}>
                {sourcesData.sources.map((source) => (
                  <Grid item xs={12} md={6} lg={4} key={source.id}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {source.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          {source.base_url}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={source.country || 'TR'} 
                            size="small" 
                            color="primary"
                          />
                          <Chip 
                            label={source.language || 'tr'} 
                            size="small" 
                            variant="outlined"
                          />
                          <Chip 
                            label={source.is_active ? 'Aktif' : 'Pasif'} 
                            size="small" 
                            color={source.is_active ? 'success' : 'error'}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">Haber kaynakları yükleniyor...</Alert>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <Typography variant="h6" gutterBottom>📈 Sentiment Analizi & Görselleştirme</Typography>
            
            {analyticsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : analyticsData && !analyticsData.error ? (
              <Grid container spacing={3}>
                {/* Sentiment Dağılımı */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Sentiment Dağılımı
                      </Typography>
                      {analyticsData.sentiment_distribution && (
                        <Box>
                          {Object.entries(analyticsData.sentiment_distribution).map(([sentiment, count]) => {
                            const total = analyticsData.total_articles || 1
                            const percentage = ((count / total) * 100).toFixed(1)
                            const color = sentiment === 'negatif' ? 'error' : 
                                        sentiment === 'pozitif' ? 'success' : 'info'
                            
                            return (
                              <Box key={sentiment} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                    {sentiment}
                                  </Typography>
                                  <Typography variant="body2">
                                    {count} ({percentage}%)
                                  </Typography>
                                </Box>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={parseFloat(percentage)} 
                                  color={color}
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                              </Box>
                            )
                          })}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Genel İstatistikler */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Genel İstatistikler
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="h4" color="primary">
                              {analyticsData.total_articles || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Analiz Edilen Haber
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="h4" color="info.main">
                              {analyticsData.average_sentiment_score || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Ortalama Sentiment
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="body2" color="textSecondary">
                        <Timeline sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                        Analiz Tarihi: {analyticsData.analysis_timestamp ? 
                          new Date(analyticsData.analysis_timestamp).toLocaleDateString('tr-TR') : 
                          'Bilinmiyor'
                        }
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* En Negatif Haberler */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        🔍 En Negatif Haberler (Sentiment Analizi)
                      </Typography>
                      {analyticsData.top_negative && analyticsData.top_negative.length > 0 ? (
                        <Grid container spacing={2}>
                          {analyticsData.top_negative.slice(0, 6).map((article, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <Card variant="outlined" sx={{ height: '100%' }}>
                                <CardContent>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {article.title}
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Chip 
                                      label={`Skor: ${article.sentiment.score}`}
                                      size="small"
                                      color="error"
                                    />
                                    <Chip 
                                      label={`Güven: ${(article.sentiment.confidence * 100).toFixed(0)}%`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Alert severity="info">Negatif haber analizi bulunamadı</Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Alert severity="warning">
                Sentiment analizi verileri bulunamadı. Lütfen backend'de analiz çalıştırın.
            </Alert>
            )}
          </TabPanel>
        </Card>
      </Container>
    </Box>
  )
}

export default App
